/**
 * Page Transitions — smooth crossfade between pages.
 *
 * Uses native View Transitions API (Chrome/Edge 111+).
 * Fallback: manual opacity fade for Safari/Firefox.
 * Pages embedding Power Form Builder always get a full page load.
 */
(function () {
  'use strict';

  const TRANSITION_DURATION = 300;
  const FULL_LOAD_SELECTOR = '.globo-formbuilder, [data-full-page-load]';
  const SCROLL_KEY = 'pt:scroll';
  let isTransitioning = false;
  // Le dernier clic l'emporte : chaque navigation a un numéro, une navigation plus récente annule les précédentes.
  let navId = 0;
  let fetchController = null;
  // Chemin + paramètres : un retour arrière qui ne change que les filtres (?filter…) doit aussi recharger le contenu.
  let currentPath = location.pathname + location.search;

  // Position de défilement mémorisée par page (sessionStorage, par URL) et restaurée au retour arrière/avant.
  // Restauration manuelle : celle du navigateur s'appliquait avant que l'ancien contenu soit revenu (sauts).
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  function readScrolls() {
    try {
      return JSON.parse(sessionStorage.getItem(SCROLL_KEY)) || {};
    } catch {
      return {};
    }
  }

  function saveScroll() {
    try {
      const positions = readScrolls();
      delete positions[location.href];
      positions[location.href] = Math.round(window.scrollY);
      const keys = Object.keys(positions);
      if (keys.length > 60) delete positions[keys[0]];
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify(positions));
    } catch {
      /* sessionStorage indisponible */
    }
  }

  // Réapplique la position tant que la page n'a pas retrouvé sa hauteur (images, sliders).
  function restoreScroll(href) {
    const y = readScrolls()[href];
    if (typeof y !== 'number') return false;
    let tries = 0;
    const apply = () => {
      window.scrollTo({ top: y, behavior: 'instant' });
      if (Math.abs(window.scrollY - y) > 2 && tries++ < 30) setTimeout(apply, 50);
    };
    apply();
    return true;
  }

  let scrollTimer = 0;
  window.addEventListener('scroll', () => {
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(saveScroll, 150);
  }, { passive: true });
  window.addEventListener('pagehide', saveScroll);

  // Retour arrière sur une page chargée normalement (hors cache du navigateur) : on restaure nous-mêmes.
  const navEntry = performance.getEntriesByType?.('navigation')?.[0];
  if (navEntry?.type === 'back_forward') {
    const restore = () => restoreScroll(location.href);
    if (document.readyState === 'complete') restore();
    else window.addEventListener('load', restore, { once: true });
  }

  // Les autres scripts (filtres de collection, variantes…) changent aussi l'URL : on suit l'adresse courante.
  ['pushState', 'replaceState'].forEach((method) => {
    const original = history[method];
    history[method] = function (...args) {
      const result = original.apply(this, args);
      currentPath = location.pathname + location.search;
      return result;
    };
  });

  document.addEventListener('click', onLinkClick);
  window.addEventListener('popstate', onPopState);

  // Sélecteur de langue / pays : Shopify fixe « return_to » au chargement, mais l'en-tête n'est pas
  // remplacé lors d'une navigation sans rechargement → on renvoie vers la page réellement affichée.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!/\/localization$/.test(action)) return;
    const returnTo = form.querySelector('input[name="return_to"]');
    if (returnTo) returnTo.value = location.pathname + location.search;
  }, true);

  function onLinkClick(event) {
    // Already handled elsewhere (e.g. collection filters Section-Render update).
    if (event.defaultPrevented) return;

    const link = event.target.closest('a[href]');
    if (!link) return;

    // Skip: modifier keys, external, new tab, anchors, excluded
    if (
      event.metaKey || event.ctrlKey || event.shiftKey ||
      link.target === '_blank' ||
      link.hostname !== location.hostname ||
      (link.pathname === location.pathname && link.hash) ||
      link.hasAttribute('data-no-transition') ||
      link.closest('[data-no-transition]')
    ) return;

    // Skip: cart links (handled by cart-drawer), checkout, accounts
    const path = link.pathname;
    if (
      path === '/cart' ||
      path.startsWith('/cart/') ||
      path.includes('/checkout') ||
      path.includes('/account')
    ) return;

    // Skip: cart icon
    if (link.closest('.header__cart-icon')) return;

    // Skip: file resources
    const ext = path.split('.').pop();
    if (['pdf', 'jpg', 'png', 'gif', 'svg', 'zip', 'js', 'css'].includes(ext)) return;

    event.preventDefault();
    // Un clic pendant un chargement n'est plus ignoré : il remplace la navigation en cours.
    saveScroll();
    navigateTo(link.href, true);
  }

  function onPopState() {
    // Hash-only change: let the browser scroll natively.
    if (location.pathname + location.search === currentPath) return;
    navigateTo(location.href, false);
  }

  async function navigateTo(url, pushState) {
    const id = ++navId;
    const isStale = () => id !== navId;
    isTransitioning = true;

    // Annule le chargement de la page demandée juste avant.
    fetchController?.abort();
    fetchController = new AbortController();

    try {
      const fetchPromise = fetchPage(url, fetchController.signal);

      if (document.startViewTransition) {
        const html = await fetchPromise;
        if (isStale()) return;
        if (!html) { fallbackNavigate(url); return; }

        // Le navigateur peut retarder l'échange de contenu (onglet en arrière-plan, rendu en pause) :
        // une transition plus récente fait alors exécuter l'ancien échange en retard → page précédente
        // affichée. On n'applique donc que la navigation la plus récente, une seule fois, et sans
        // animation si le navigateur n'a rien fait au bout de 500 ms.
        let applied = false;
        const apply = () => {
          if (applied || isStale()) return;
          applied = true;
          applyNewContent(html, url, pushState);
        };
        const transition = document.startViewTransition(apply);
        const fallbackTimer = setTimeout(() => {
          if (applied || isStale()) return;
          transition.skipTransition?.();
          apply();
        }, 500);

        // On n'attend jamais indéfiniment une animation qui peut rester bloquée.
        await Promise.race([transition.finished.catch(() => {}), wait(1500)]);
        clearTimeout(fallbackTimer);
      } else {
        const main = document.querySelector('#MainContent');
        if (!main) { fallbackNavigate(url); return; }

        main.classList.add('page-leaving');
        await wait(TRANSITION_DURATION);

        const html = await fetchPromise;
        if (isStale()) return;
        if (!html) { fallbackNavigate(url); return; }

        applyNewContent(html, url, pushState);

        main.classList.remove('page-leaving');
        main.classList.add('page-entering');

        await wait(TRANSITION_DURATION);
        main.classList.remove('page-entering');
      }
    } catch (err) {
      if (isStale()) return;
      console.error('Page transition error:', err);
      fallbackNavigate(url);
    } finally {
      if (!isStale()) isTransitioning = false;
    }
  }

  async function fetchPage(url, signal) {
    try {
      const res = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal,
      });
      if (!res.ok) return null;
      return await res.text();
    } catch {
      return null;
    }
  }

  function applyNewContent(html, url, pushState) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const currentMain = document.querySelector('#MainContent');
    const newMain = doc.querySelector('#MainContent');
    if (!currentMain || !newMain) {
      // No swappable #MainContent (e.g. metaobject pages): do a real navigation.
      window.location.href = url;
      return;
    }
    // Third-party embeds (Power Form Builder) only init on a full page load.
    if (newMain.querySelector(FULL_LOAD_SELECTOR)) {
      window.location.href = url;
      return;
    }
    currentMain.innerHTML = newMain.innerHTML;
    for (const attr of newMain.attributes) {
      currentMain.setAttribute(attr.name, attr.value);
    }

    // Update header dark/transparent state
    const currentHeader = document.querySelector('#header-component');
    const newHeader = doc.querySelector('#header-component');
    if (currentHeader && newHeader) {
      currentHeader.className = newHeader.className;
    }

    const newTitle = doc.querySelector('title');
    if (newTitle) document.title = newTitle.textContent;

    const currentCanonical = document.querySelector('link[rel="canonical"]');
    const newCanonical = doc.querySelector('link[rel="canonical"]');
    if (currentCanonical && newCanonical) {
      currentCanonical.href = newCanonical.href;
    }

    if (pushState) {
      window.history.pushState({}, '', url);
    }

    // Track the current path so onPopState can ignore hash-only changes.
    const target = new URL(url, location.origin);
    currentPath = target.pathname + target.search;

    // Nouvelle page : en haut. Retour arrière/avant : position mémorisée (après réinitialisation des composants).
    if (pushState) window.scrollTo({ top: 0, behavior: 'instant' });
    reinitialize();
    if (!pushState && !restoreScroll(target.href)) window.scrollTo({ top: 0, behavior: 'instant' });
  }

  function reinitialize() {
    if (typeof window.initCvSliders === 'function') {
      window.initCvSliders();
    }

    document.dispatchEvent(new CustomEvent('page:transition'));

    const main = document.querySelector('#MainContent');
    if (!main) return;

    main.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');
      for (const attr of oldScript.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }
      newScript.textContent = oldScript.textContent;
      oldScript.replaceWith(newScript);
    });
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function fallbackNavigate(url) {
    isTransitioning = false;
    window.location.href = url;
  }
})();